import Phaser from 'phaser';
import type { ContractFixtureEnvelope } from '../../contract/types';
import { getHitsRequired } from '../world/difficulty';
import { recordAction, saveWorldState } from '../world/worldStorage';
import type { PersistedWorldState, SessionConfig, Side } from '../world/types';
import { actionTypeForSide } from '../world/types';
import { GAME_TIMING, WORLD_LAYOUT } from '../theme';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import { WORLD_SCENE_KEY } from '../sceneKeys';
import { a11yBus, A11Y_EVENTS } from '../../accessibility/a11yBus';

export interface WorldSceneData {
  contractVendored: boolean;
  fixture?: ContractFixtureEnvelope;
  sessionConfig: SessionConfig;
  worldState: PersistedWorldState;
}

/**
 * CP21 gameplay scene (Brief §9): Beornings chop right, Lothwen plant
 * left, Minas Tirith centered at world x=0. Quota-based session, no timer
 * (§9 - the old "1,000 steps = 1 minute" economy is explicitly dropped).
 * Consumes the CP14 contract via a loaded fixture ONLY (never a live
 * endpoint - v001.1 concern) for the split-bar HUD; consumes local
 * persisted world state (see `world/worldStorage.ts`) for the actual
 * chop/plant progress, which is permanent and NOT the same thing as the
 * season-scoped Tier 2 economy the fixture's `sides.*.totalPoints` reflect.
 */
export class WorldScene extends Phaser.Scene {
  private worldState!: PersistedWorldState;
  private side!: Side;
  private hrOverCeiling = false;
  private quotaRemaining = 0;

  private player!: Phaser.GameObjects.Image;
  private skySprite!: Phaser.GameObjects.TileSprite;
  private hillsSprite!: Phaser.GameObjects.TileSprite;

  private tileSprites = new Map<string, Phaser.GameObjects.Image>();
  private activeTileIndex = 0;
  private hitProgress = 0;
  private hitsRequiredForActiveTile = 0;
  private lastHitAt = 0;

  private promptText!: Phaser.GameObjects.Text;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private actionKey!: Phaser.Input.Keyboard.Key;
  /** Driven by the real DOM movement buttons via a11yBus (CP22 fix) - no
   *  longer set by canvas-drawn Phaser text objects. */
  private touchLeft = false;
  private touchRight = false;
  private actionsThisSession = 0;
  /** Bound a11yBus listener refs, kept so they can be removed on shutdown. */
  private onA11yMoveLeft = (active: boolean): void => {
    this.touchLeft = active;
  };
  private onA11yMoveRight = (active: boolean): void => {
    this.touchRight = active;
  };
  private onA11yAct = (): void => {
    this.tryInteract();
  };

  constructor() {
    super(WORLD_SCENE_KEY);
  }

  create(data: WorldSceneData): void {
    this.worldState = data.worldState;
    this.side = data.sessionConfig.side;
    this.hrOverCeiling = data.sessionConfig.hrOverCeiling;
    this.quotaRemaining = data.sessionConfig.quota;

    this.spawnParallax();
    this.spawnMinasTirith();
    this.renderSideTiles('beornings');
    this.renderSideTiles('lothwen');
    this.setupActiveTileTarget();
    this.spawnPlayer();
    this.setupInput();
    this.setupCamera();

    this.promptText = this.add
      .text(0, 0, 'SPACE / tap ACTION', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#e0c97b',
      })
      .setOrigin(0.5, 1)
      .setVisible(false);

    // Broadcast initial quota so HudScene (launched separately by BootScene) can render it.
    this.events.emit('quotaChanged', this.quotaRemaining);
  }

  update(_time: number, delta: number): void {
    this.handleMovement(delta);
    this.updatePromptVisibility();

    const parallaxScrollX = this.cameras.main.scrollX;
    this.skySprite.tilePositionX = parallaxScrollX * 0.1;
    this.hillsSprite.tilePositionX = parallaxScrollX * 0.3;

    if (Phaser.Input.Keyboard.JustDown(this.actionKey)) {
      this.tryInteract();
    }
  }

  // ---- setup -------------------------------------------------------------

  private spawnParallax(): void {
    this.skySprite = this.add
      .tileSprite(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 'parallax.sky')
      .setScrollFactor(0)
      .setDepth(-30);
    this.hillsSprite = this.add
      .tileSprite(GAME_WIDTH / 2, WORLD_LAYOUT.groundY - 40, GAME_WIDTH, 220, 'parallax.hills')
      .setOrigin(0.5, 1)
      .setScrollFactor(0)
      .setDepth(-20);
  }

  private spawnMinasTirith(): void {
    this.add.image(0, WORLD_LAYOUT.groundY, 'minasTirith.bg').setOrigin(0.5, 1).setDepth(-10);
  }

  private worldXForTile(side: Side, tileIndex: number): number {
    const sign = side === 'beornings' ? 1 : -1;
    return sign * tileIndex * WORLD_LAYOUT.tileSpacingPx;
  }

  private spriteKeyForTile(side: Side, resolved: boolean): string {
    if (side === 'beornings') return resolved ? 'tree.stump' : 'tree.corrupted';
    return resolved ? 'tree.planted' : 'plot.empty';
  }

  /** Renders tiles 1..(frontier + renderHorizon) for a side. Only the session's own `this.side` is interactive. */
  private renderSideTiles(side: Side): void {
    const sideState = this.worldState[side];
    const renderCount = sideState.frontierPosition + WORLD_LAYOUT.renderHorizonTiles;
    for (let i = 1; i <= renderCount; i += 1) {
      const resolved = i <= sideState.frontierPosition;
      const key = this.spriteKeyForTile(side, resolved);
      const image = this.add
        .image(this.worldXForTile(side, i), WORLD_LAYOUT.groundY, key)
        .setOrigin(0.5, 1)
        .setDepth(0);
      this.tileSprites.set(`${side}:${i}`, image);
    }
  }

  private setupActiveTileTarget(): void {
    const sideState = this.worldState[this.side];
    this.activeTileIndex = sideState.frontierPosition + 1;
    this.hitsRequiredForActiveTile = getHitsRequired(this.activeTileIndex, this.hrOverCeiling);
    // Ensure the active tile (and a render buffer beyond it) exists even if
    // the persisted frontier was at the very edge of what renderSideTiles drew.
    this.ensureTileRendered(this.side, this.activeTileIndex + WORLD_LAYOUT.renderHorizonTiles);
  }

  private ensureTileRendered(side: Side, uptoIndex: number): void {
    const sideState = this.worldState[side];
    for (let i = sideState.frontierPosition + 1; i <= uptoIndex; i += 1) {
      const mapKey = `${side}:${i}`;
      if (this.tileSprites.has(mapKey)) continue;
      const image = this.add
        .image(this.worldXForTile(side, i), WORLD_LAYOUT.groundY, this.spriteKeyForTile(side, false))
        .setOrigin(0.5, 1)
        .setDepth(0);
      this.tileSprites.set(mapKey, image);
    }
  }

  /** Brief §9: "a player entering the mini-game drops in at the tree's edge ... and walks back toward Minas Tirith." */
  private spawnPlayer(): void {
    const spawnX = this.worldXForTile(this.side, this.activeTileIndex);
    const textureKey = this.side === 'beornings' ? 'player.male' : 'player.female';
    this.player = this.add.image(spawnX, WORLD_LAYOUT.groundY, textureKey).setOrigin(0.5, 1).setDepth(10);
    // Face back toward Minas Tirith (position 0): Beornings stand at +x so
    // facing the city means facing left (flip); Lothwen stand at -x so
    // facing the city means facing right (no flip), assuming the source art
    // faces right by default.
    this.player.setFlipX(this.side === 'beornings');
  }

  private setupInput(): void {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.actionKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.setupAccessibleControls();
  }

  /**
   * CP22 fix: the on-screen movement/ACT controls used to be Phaser-canvas
   * text objects (`makeButton`) that scaled down with the whole game via
   * `Scale.FIT` - at the 360px viewport the ACT button measured 25x15 CSS
   * px, far under the WCAG 44x44px minimum (Vera's HIGH finding), and
   * canvas objects can never carry `aria-label`s or receive real
   * keyboard/AT focus (part of Vera's CRITICAL accessibility finding).
   *
   * The controls now live in the DOM (`AccessibilityLayer.ts`, mounted
   * once in `main.ts`, sized via `.game-control-btn` in `style.css`) and
   * talk to this scene over `a11yBus` instead of Phaser's own input system.
   */
  private setupAccessibleControls(): void {
    a11yBus.on(A11Y_EVENTS.MOVE_LEFT, this.onA11yMoveLeft);
    a11yBus.on(A11Y_EVENTS.MOVE_RIGHT, this.onA11yMoveRight);
    a11yBus.on(A11Y_EVENTS.ACT, this.onA11yAct);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.teardownAccessibleControls, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.teardownAccessibleControls, this);
  }

  private teardownAccessibleControls(): void {
    a11yBus.off(A11Y_EVENTS.MOVE_LEFT, this.onA11yMoveLeft);
    a11yBus.off(A11Y_EVENTS.MOVE_RIGHT, this.onA11yMoveRight);
    a11yBus.off(A11Y_EVENTS.ACT, this.onA11yAct);
  }

  private setupCamera(): void {
    const beorningsHorizon = this.worldXForTile(
      'beornings',
      this.worldState.beornings.frontierPosition + WORLD_LAYOUT.renderHorizonTiles + 2,
    );
    const lothwenHorizon = this.worldXForTile(
      'lothwen',
      this.worldState.lothwen.frontierPosition + WORLD_LAYOUT.renderHorizonTiles + 2,
    );
    const minX = Math.min(lothwenHorizon, -GAME_WIDTH / 2);
    const maxX = Math.max(beorningsHorizon, GAME_WIDTH / 2);
    this.cameras.main.setBounds(minX - GAME_WIDTH / 2, 0, maxX - minX + GAME_WIDTH, GAME_HEIGHT);
    this.cameras.main.startFollow(this.player, true, GAME_TIMING.cameraFollowLerp, GAME_TIMING.cameraFollowLerp);
  }

  // ---- per-frame ----------------------------------------------------------

  private handleMovement(delta: number): void {
    const left = this.cursors.left?.isDown || this.touchLeft;
    const right = this.cursors.right?.isDown || this.touchRight;
    const distance = (GAME_TIMING.playerSpeedPxPerSec * delta) / 1000;

    if (left === right) return; // neither or both held - no movement
    if (left) {
      this.player.x -= distance;
      this.player.setFlipX(true);
    } else {
      this.player.x += distance;
      this.player.setFlipX(false);
    }

    const bounds = this.cameras.main.getBounds();
    this.player.x = Phaser.Math.Clamp(this.player.x, bounds.x, bounds.right);
  }

  private activeTileWorldX(): number {
    return this.worldXForTile(this.side, this.activeTileIndex);
  }

  private updatePromptVisibility(): void {
    const distance = Math.abs(this.player.x - this.activeTileWorldX());
    const inRange = distance <= WORLD_LAYOUT.interactRadiusPx;
    this.promptText.setVisible(inRange);
    if (inRange) {
      this.promptText.setPosition(this.player.x, this.player.y - 80);
    }
  }

  private tryInteract(): void {
    const distance = Math.abs(this.player.x - this.activeTileWorldX());
    if (distance > WORLD_LAYOUT.interactRadiusPx) return;

    if (this.quotaRemaining <= 0) {
      const message = 'No quota left - log more steps to earn more.';
      this.events.emit('toast', message);
      a11yBus.emit(A11Y_EVENTS.TOAST, message);
      return;
    }

    const now = this.time.now;
    if (now - this.lastHitAt < GAME_TIMING.chopHitFlashMs) return;
    this.lastHitAt = now;

    this.hitProgress += 1;
    this.flashActiveTile();

    if (this.hitProgress >= this.hitsRequiredForActiveTile) {
      this.resolveActiveTile();
    }
  }

  private flashActiveTile(): void {
    const sprite = this.tileSprites.get(`${this.side}:${this.activeTileIndex}`);
    if (!sprite) return;
    this.tweens.add({
      targets: sprite,
      scale: 1.12,
      duration: GAME_TIMING.chopHitFlashMs / 2,
      yoyo: true,
    });
  }

  private resolveActiveTile(): void {
    const tileIndex = this.activeTileIndex;
    const type = actionTypeForSide(this.side);
    this.worldState = recordAction(this.worldState, this.side, type, new Date().toISOString());
    saveWorldState(window.localStorage, this.worldState);
    this.quotaRemaining -= 1;
    this.events.emit('quotaChanged', this.quotaRemaining);
    this.events.emit('worldUpdated', this.worldState);

    const sprite = this.tileSprites.get(`${this.side}:${tileIndex}`);
    if (sprite) {
      const resolvedKey = this.spriteKeyForTile(this.side, true);
      if (this.side === 'beornings') {
        this.tweens.add({
          targets: sprite,
          angle: 85,
          duration: GAME_TIMING.treeFallMs,
          onComplete: () => {
            sprite.setTexture(resolvedKey);
            sprite.setAngle(0);
          },
        });
        this.spawnParticleBurst(sprite.x, sprite.y - 30, 'particle.chip');
      } else {
        sprite.setTexture(resolvedKey).setScale(0.2);
        this.tweens.add({ targets: sprite, scale: 1, duration: GAME_TIMING.plantGrowMs, ease: 'Back.Out' });
        this.spawnParticleBurst(sprite.x, sprite.y - 20, 'particle.leaf');
      }
    }

    this.hitProgress = 0;
    this.activeTileIndex = tileIndex + 1;
    this.hitsRequiredForActiveTile = getHitsRequired(this.activeTileIndex, this.hrOverCeiling);
    this.ensureTileRendered(this.side, this.activeTileIndex + WORLD_LAYOUT.renderHorizonTiles);

    this.actionsThisSession += 1;
    a11yBus.emit(A11Y_EVENTS.TILE_RESOLVED, {
      side: this.side,
      frontierPosition: tileIndex,
      quotaRemaining: this.quotaRemaining,
      actionsThisSession: this.actionsThisSession,
    });
  }

  private spawnParticleBurst(x: number, y: number, textureKey: string): void {
    const emitter = this.add.particles(x, y, textureKey, {
      speed: { min: 40, max: 120 },
      angle: { min: 220, max: 320 },
      lifespan: 500,
      quantity: 10,
      scale: { start: 1, end: 0 },
      gravityY: 200,
    });
    this.time.delayedCall(600, () => emitter.destroy());
  }
}
